/**
 * Client state. The server is the real state holder — this keeps the latest view it
 * was sent, plus purely local things: the chosen sort preset, any custom hand order,
 * and the countdown for the open claim window. See scope.md §6.
 */

import { create } from 'zustand';
import { contractFor } from '@garbage/engine';
import type {
  GameEvent, Locale, PlayerId, PlayerView, ReactionId, RoomRules, ServerErrorCode,
} from '@garbage/shared';
import type { LobbyPlayer, ServerMessage, WireAction } from './protocol.js';
import { type PackId, loadPack, savePack } from './packs.js';
import { groupingFor, reconcileOrder } from './handOrder.js';
import { loadLocale, loadSound, saveLocale, saveSound } from './settings.js';
import { playSound, soundFor, unlockAudio } from './sound.js';
import { countWin } from './wins.js';

/** A reaction someone sent, kept briefly so it can be shown next to them. scope.md §8.3. */
export type ShownReaction = { key: number; from: PlayerId; reaction: ReactionId; at: number };

/** How long a reaction stays on screen. */
export const REACTION_SHOW_MS = 2500;

export type Screen = 'home' | 'lobby' | 'briefing' | 'table' | 'round-over' | 'match-over';

/** Where the rejoin token is kept, so locking the phone does not lose the seat. */
const SEAT_KEY = 'garbage.seat';

export type SavedSeat = { code: string; token: string };

export function loadSeat(): SavedSeat | null {
  try {
    const raw = localStorage.getItem(SEAT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.code === 'string' && typeof parsed?.token === 'string') return parsed;
    return null;
  } catch {
    // Private browsing, or storage blocked. Not being able to rejoin is survivable.
    return null;
  }
}

export function saveSeat(seat: SavedSeat | null): void {
  try {
    if (seat) localStorage.setItem(SEAT_KEY, JSON.stringify(seat));
    else localStorage.removeItem(SEAT_KEY);
  } catch {
    /* ignore */
  }
}

export type Transport = {
  send(message: unknown): void;
  close(): void;
};

export type GameStore = {
  // connection
  connected: boolean;
  transport: Transport | null;
  /** The server's English wording; shown translated through `errorCode` where there is one. */
  error: string | null;
  errorCode: ServerErrorCode | null;

  // identity
  code: string | null;
  you: string | null;
  host: string | null;
  token: string | null;

  // state from the server
  screen: Screen;
  view: PlayerView | null;
  legal: string[];
  lobby: LobbyPlayer[];
  rules: RoomRules | null;
  lastEvents: GameEvent[];
  ready: string[];

  // claim countdown
  claimClosesAt: number | null;
  /** When the player on turn is taken out for doing nothing. scope.md §6.4. */
  idleOutAt: number | null;
  /** Absent players the host may now remove. */
  kickable: string[];

  // local display preferences
  customOrder: string[] | null;
  /** Purely cosmetic and never sent to the server. scope.md §9. */
  pack: PackId;
  reducedMotion: boolean;
  /** This device's language. scope.md §10. */
  locale: Locale;
  /** Off until turned on. */
  sound: boolean;
  /** Reactions still on screen, newest last. */
  reactions: ShownReaction[];

  // actions
  attach(transport: Transport): void;
  detach(): void;
  receive(message: ServerMessage): void;
  send(message: unknown): void;
  act(action: WireAction): void;
  setCustomOrder(order: string[] | null): void;
  setPack(pack: PackId): void;
  setLocale(locale: Locale): void;
  setSound(on: boolean): void;
  dismissError(): void;
  reset(): void;
};

export const useGame = create<GameStore>((set, get) => ({
  connected: false,
  transport: null,
  error: null,
  errorCode: null,

  code: null,
  you: null,
  host: null,
  token: null,

  screen: 'home',
  view: null,
  legal: [],
  lobby: [],
  rules: null,
  lastEvents: [],
  ready: [],

  claimClosesAt: null,
  idleOutAt: null,
  kickable: [],

  customOrder: null,
  pack: loadPack(),
  reducedMotion: false,
  locale: loadLocale(),
  sound: loadSound(),
  reactions: [],

  attach(transport) {
    set({ transport, connected: true });
  },

  detach() {
    set({ transport: null, connected: false });
  },

  send(message) {
    get().transport?.send(message);
  },

  act(action) {
    get().send({ t: 'action', action });
  },

  receive(message) {
    switch (message.t) {
      case 'joined': {
        saveSeat({ code: message.code, token: message.token });
        set({
          code: message.code, you: message.you, token: message.token, host: message.host,
          error: null, errorCode: null,
        });
        // Stay on the table if a game is already running (this was a rejoin).
        if (get().screen === 'home') set({ screen: 'lobby' });
        return;
      }

      case 'lobby': {
        set({
          lobby: message.players, rules: message.rules, host: message.host, code: message.code,
        });
        if (get().screen === 'home') set({ screen: 'lobby' });
        // "Play again": the room has gone back to the lobby, so everyone follows it there.
        if (message.inGame === false && get().screen !== 'lobby') {
          set({
            screen: 'lobby', view: null, legal: [], lastEvents: [], ready: [], customOrder: null,
            claimClosesAt: null, idleOutAt: null, kickable: [],
          });
        }
        return;
      }

      case 'state': {
        const view = message.view;
        // MATCH_WON arrives once, with the change that ended the match; a reconnect
        // sends state without it, so nothing is counted twice.
        countWin(message.events ?? [], view.you);
        if (get().sound) {
          const sound = soundFor(message.events ?? [], view, get().view);
          if (sound) playSound(sound);
        }
        const screen: Screen = view.status === 'round-over' ? 'round-over'
          : view.status === 'match-over' ? 'match-over'
            : view.status === 'briefing' ? 'briefing'
              : 'table';
        set({
          view,
          legal: message.legal ?? [],
          lastEvents: message.events ?? [],
          ready: message.ready ?? [],
          screen,
          claimClosesAt: message.claimClosesInMs === null || message.claimClosesInMs === undefined
            ? null
            : Date.now() + message.claimClosesInMs,
          idleOutAt: message.idleOutInMs === null || message.idleOutInMs === undefined
            ? null
            : Date.now() + message.idleOutInMs,
          kickable: message.kickable ?? [],
          // A fresh round starts from the logical order again, but an order set during
          // the briefing must survive into play — sorting there is the whole point. Cards
          // picked up or given as a penalty join on the left, where they will be noticed.
          customOrder: view.status === 'playing' || view.status === 'briefing'
            ? reconcileOrder(
              view.players.find((p) => p.id === view.you)?.hand ?? [],
              get().customOrder,
              groupingFor(contractFor(view.round, view.rules).requirements.map((r) => r.kind)),
            )
            : null,
        });
        return;
      }

      case 'error': {
        // An error before we are in a room is a failed rejoin of a game that has since
        // gone. The player did nothing; forget the stale seat rather than alarm them.
        if (get().code === null) {
          saveSeat(null);
          return;
        }
        set({ error: message.message, errorCode: message.code ?? null });
        return;
      }

      case 'closed':
        saveSeat(null);
        set({
          error: message.reason, errorCode: message.code ?? null,
          screen: 'home', view: null, code: null, token: null,
        });
        return;

      case 'reaction': {
        const at = Date.now();
        const shown = get().reactions.filter((r) => at - r.at < REACTION_SHOW_MS && r.from !== message.from);
        set({ reactions: [...shown, { key: at + Math.random(), from: message.from, reaction: message.reaction, at }] });
        if (get().sound && message.from !== get().you) playSound('reaction');
        return;
      }
    }
  },

  setCustomOrder(order) {
    set({ customOrder: order });
  },

  setPack(pack) {
    savePack(pack);
    set({ pack });
  },

  setLocale(locale) {
    saveLocale(locale);
    set({ locale });
  },

  setSound(on) {
    saveSound(on);
    // Turning sound on is a tap, which is exactly when a phone lets a page start audio.
    if (on) unlockAudio();
    set({ sound: on });
  },

  dismissError() {
    set({ error: null, errorCode: null });
  },

  reset() {
    saveSeat(null);
    set({
      code: null, you: null, host: null, token: null, screen: 'home',
      view: null, legal: [], lobby: [], rules: null, lastEvents: [],
      claimClosesAt: null, idleOutAt: null, kickable: [], customOrder: null, error: null, errorCode: null,
      ready: [], reactions: [],
    });
  },
}));

/**
 * Should we try to reclaim a saved seat on connecting?
 *
 * Yes when the page was simply reloaded, or when the invite link points at the room
 * we were already in. **No** when an invite link names a different room: following a
 * friend's link must not silently drop you back into your old game.
 */
export function shouldRejoin(seat: SavedSeat | null, linkCode: string | null): boolean {
  if (!seat) return false;
  if (!linkCode) return true;
  return linkCode === seat.code;
}

/** How long to wait before the nth reconnection attempt. */
export function retryDelay(attempt: number): number {
  // Quick at first — a locked phone reconnects the moment it wakes — then backing
  // off so a server that is genuinely down is not hammered.
  return Math.min(500 * 2 ** attempt, 10_000);
}

export type Connection = { retryNow: () => void; close: () => void };

/**
 * Keep a WebSocket open, reconnecting when it drops.
 *
 * This matters more on a phone than anywhere else: locking the screen kills the
 * socket, and without this the game would simply be dead when you came back to it.
 * On reconnecting we reclaim the seat, so the round carries on where it was.
 */
export function startConnection(url: string, linkCode: string | null = null): Connection {
  let socket: WebSocket | null = null;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let closedForGood = false;

  const open = (): void => {
    if (closedForGood) return;
    socket = new WebSocket(url);

    socket.addEventListener('open', () => {
      attempt = 0;
      const live = socket;
      if (!live) return;
      useGame.getState().attach({
        send: (message) => live.send(JSON.stringify(message)),
        close: () => live.close(),
      });

      const seat = loadSeat();
      if (shouldRejoin(seat, linkCode) && seat) {
        live.send(JSON.stringify({ t: 'rejoin', code: seat.code, token: seat.token }));
      } else if (linkCode) {
        // The link wins: forget the stale seat so the join screen is what appears.
        saveSeat(null);
      }
    });

    socket.addEventListener('message', (event) => {
      try {
        useGame.getState().receive(JSON.parse(String(event.data)));
      } catch {
        // A message we cannot parse is not worth crashing the page over.
      }
    });

    socket.addEventListener('close', () => {
      useGame.getState().detach();
      schedule();
    });

    socket.addEventListener('error', () => {
      try {
        socket?.close();
      } catch {
        /* already gone */
      }
    });
  };

  const schedule = (): void => {
    if (closedForGood) return;
    clearTimeout(timer);
    timer = setTimeout(open, retryDelay(attempt));
    attempt += 1;
  };

  const retryNow = (): void => {
    clearTimeout(timer);
    attempt = 0;
    if (socket && socket.readyState === WebSocket.OPEN) return;
    open();
  };

  open();

  if (typeof document !== 'undefined') {
    // Coming back to a phone that has been asleep is the commonest way to find a
    // dead socket, and the commonest moment to want it back.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') retryNow();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', retryNow);
  }

  return {
    retryNow,
    close() {
      closedForGood = true;
      clearTimeout(timer);
      socket?.close();
    },
  };
}

export function defaultSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}
